import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './BookingCalendar.css';

function BookingCalendar() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Fetch rooms and ALL bookings (from all users) in parallel
      const [roomsData, bookingsData] = await Promise.all([
        conferenceRoomApi.getAll(false), 
        conferenceRoomApi.getAllBookingRequests()  
      ]);

      const roomsList = Array.isArray(roomsData?.data) ? roomsData.data :
                       Array.isArray(roomsData) ? roomsData :
                       Array.isArray(roomsData?.content) ? roomsData.content : [];

      // Use ALL bookings for calendar
      const allBookings = Array.isArray(bookingsData) ? bookingsData : [];

      console.log('Calendar loaded:', allBookings.length, 'bookings from', roomsList.length, 'rooms');
      setBookings(allBookings);
      setRooms(roomsList);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load calendar data', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get week dates
  const getWeekDates = (date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start from Sunday

    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // Get month dates
  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const dates = [];
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    let currentDay = new Date(startDate);
    while (currentDay <= lastDay || currentDay.getDay() !== 0) {
      dates.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return dates;
  };

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const monthDates = useMemo(() => getMonthDates(currentDate), [currentDate]);
  const displayDates = viewMode === 'week' ? weekDates : monthDates;

  // Filter bookings for display
  const filteredBookings = useMemo(() => {
    let filtered = bookings;

    if (selectedRoom !== 'all') {
      filtered = filtered.filter(b => b.roomId === selectedRoom);
    }

    // Only show bookings in the current view period
    const startDate = displayDates[0];
    const endDate = displayDates[displayDates.length - 1];

    filtered = filtered.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return bookingDate >= startDate && bookingDate <= endDate;
    });

    return filtered;
  }, [bookings, selectedRoom, displayDates]);

  // Get bookings for a specific date (from filtered bookings in current view)
  const getBookingsForDate = (date) => {
    return filteredBookings.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return bookingDate.toDateString() === date.toDateString();
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  };

  // Get ALL bookings for a specific date (including future, not limited to current view)
  const getAllBookingsForDate = (date) => {
    let filtered = bookings;

    // Apply room filter if selected
    if (selectedRoom !== 'all') {
      filtered = filtered.filter(b => b.roomId === selectedRoom);
    }

    return filtered.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return bookingDate.toDateString() === date.toDateString();
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  };

  // Handle date click to show modal
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDayModal(true);
  };

  // Close modal
  const closeDayModal = () => {
    setShowDayModal(false);
    setSelectedDate(null);
  };

  // Get booking status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return '#10b981';
      case 'PENDING': return '#f59e0b';
      case 'REJECTED': return '#ef4444';
      case 'CANCELLED': return '#6b7280';
      default: return '#3b82f6';
    }
  };

  // Get room name
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.roomName : 'Unknown Room';
  };

  // Navigate calendar
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date for display
  const formatDateHeader = () => {
    if (viewMode === 'week') {
      const start = weekDates[0];
      const end = weekDates[6];
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  if (isLoading && bookings.length === 0) {
    return (
      <div className="admin-page">
        <PageHeader title="Booking Calendar" />
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p>Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <PageHeader
        title="Booking Calendar"
        subtitle="View and manage conference room bookings"
      />

      {/* Calendar Controls */}
      <div className="calendar-controls">
        <div className="calendar-nav">
          <button className="nav-btn" onClick={goToPrevious}>‹</button>
          <h2 className="calendar-title">{formatDateHeader()}</h2>
          <button className="nav-btn" onClick={goToNext}>›</button>
          <button className="today-btn" onClick={goToToday}>Today</button>
        </div>

        <div className="calendar-filters">
          <select
            value={selectedRoom}
            onChange={(e) => setSelectedRoom(e.target.value)}
            className="room-filter"
          >
            <option value="all">All Rooms</option>
            {rooms.map(room => (
              <option key={room.id} value={room.id}>{room.roomName}</option>
            ))}
          </select>

          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#10b981' }}></span>
          <span>Approved</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#f59e0b' }}></span>
          <span>Pending</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#ef4444' }}></span>
          <span>Rejected</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#6b7280' }}></span>
          <span>Cancelled</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className={`calendar-grid ${viewMode}`}>
        {/* Day Headers */}
        {viewMode === 'week' && (
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
              <div key={idx} className="day-header">
                {day}
              </div>
            ))}
          </div>
        )}

        {viewMode === 'month' && (
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
              <div key={idx} className="day-header">
                {day}
              </div>
            ))}
          </div>
        )}

        {/* Calendar Days */}
        <div className="calendar-body">
          {displayDates.map((date, idx) => {
            const dayBookings = getBookingsForDate(date);
            const today = isToday(date);
            const currentMonth = isCurrentMonth(date);

            return (
              <div
                key={idx}
                className={`calendar-day ${today ? 'today' : ''} ${!currentMonth && viewMode === 'month' ? 'other-month' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">
                  {date.getDate()}
                </div>

                <div className="day-bookings">
                  {dayBookings.slice(0, viewMode === 'week' ? 10 : 3).map((booking, bidx) => (
                    <div
                      key={bidx}
                      className="booking-item"
                      style={{ borderLeftColor: getStatusColor(booking.status) }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateClick(date);
                      }}
                    >
                      <div className="booking-time">
                        {new Date(booking.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="booking-room">{getRoomName(booking.roomId)}</div>
                      {viewMode === 'week' && (
                        <div className="booking-purpose">
                          {booking.notes && booking.notes.length > 30
                            ? booking.notes.substring(0, 30) + '...'
                            : booking.notes}
                        </div>
                      )}
                    </div>
                  ))}

                  {dayBookings.length > (viewMode === 'week' ? 10 : 3) && (
                    <div className="more-bookings" onClick={(e) => { e.stopPropagation(); handleDateClick(date); }}>
                      +{dayBookings.length - (viewMode === 'week' ? 10 : 3)} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Statistics */}
      <div className="calendar-stats">
        <div className="stat-card">
          <div className="stat-number">{filteredBookings.length}</div>
          <div className="stat-label">Total Bookings</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {filteredBookings.filter(b => b.status === 'APPROVED').length}
          </div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {filteredBookings.filter(b => b.status === 'PENDING').length}
          </div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {filteredBookings.filter(b => b.status === 'REJECTED').length}
          </div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      {/* Day Bookings Modal */}
      {showDayModal && selectedDate && (
        <div className="day-modal-overlay" onClick={closeDayModal}>
          <div className="day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="day-modal-header">
              <h3>
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <button className="day-modal-close" onClick={closeDayModal}>×</button>
            </div>

            <div className="day-modal-body">
              {(() => {
                const dayBookings = getAllBookingsForDate(selectedDate);
                if (dayBookings.length === 0) {
                  return (
                    <div className="day-modal-empty">
                      <p>No bookings for this day</p>
                      <button
                        className="book-room-btn"
                        onClick={() => navigate('/admin/community/book-room')}
                      >
                        Book a Room
                      </button>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="day-modal-summary">
                      <span className="summary-total">{dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}</span>
                      <span className="summary-approved">
                        {dayBookings.filter(b => b.status === 'APPROVED').length} approved
                      </span>
                      <span className="summary-pending">
                        {dayBookings.filter(b => b.status === 'PENDING').length} pending
                      </span>
                    </div>

                    <div className="day-modal-bookings">
                      {dayBookings.map((booking, idx) => (
                        <div
                          key={idx}
                          className="day-modal-booking-item"
                          style={{ borderLeftColor: getStatusColor(booking.status) }}
                        >
                          <span className="modal-booking-time">
                            {new Date(booking.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {new Date(booking.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="modal-booking-room">{getRoomName(booking.roomId)}</span>
                          <span
                            className="modal-booking-status"
                            style={{ backgroundColor: getStatusColor(booking.status) }}
                          >
                            {booking.status}
                          </span>
                          <div className="modal-booking-details">
                            <span className="modal-booking-id">
                              <span className="id-label">ID:</span> {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                            </span>
                            <span className="modal-booking-user">
                              <span className="user-label">By:</span> {booking.bookedByEmail || booking.userEmail || 'Unknown'}
                            </span>
                            {booking.notes && (
                              <span className="modal-booking-purpose">
                                <span className="purpose-label">Purpose:</span> {booking.notes.length > 40 ? booking.notes.substring(0, 40) + '...' : booking.notes}
                              </span>
                            )}
                            {booking.recurringBookingsCount > 1 && (
                              <span className="modal-booking-recurring">
                                <span className="recurring-badge">Recurring</span>
                                <span className="recurring-info">
                                  {booking.recurringBookingsCount}x until {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="day-modal-footer">
              <button
                className="modal-btn modal-btn-secondary"
                onClick={closeDayModal}
              >
                Close
              </button>
              <button
                className="modal-btn modal-btn-primary"
                onClick={() => navigate('/admin/community/book-room')}
              >
                Book a Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingCalendar;
